import {
  SCRIPTED_AGENT_ID,
  ScriptNode,
  type Script,
} from '@/config/scripts/prudential-annual-checkin';
import { CUSTOMER_NAME as GRAB_DEFAULT_CUSTOMER_NAME } from '@/config/scripts/grab-restaurant-onboarding';
import { getScript, scriptRegistry } from '@/config/scripts';
import { getScriptPrompt } from '@/config/scripts/prompts';
import api from '@/utils/ky';
import { ScriptEngine, ScriptState } from '@/utils/scriptEngine';
import { playScriptFirstMessage } from '@/utils/tts';
import { useConversation } from '@elevenlabs/react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CallMode, CallType } from '../routes/agent/RolePlay';
import {
  CallState,
  LoadingState,
  AccentType,
  type Transcript,
  type BaseAgentCallProps,
  type AgentCallInterface,
} from './types/agentCall.types';

// Voice ID mapping
const VOICE_IDS = {
  [AccentType.BRITISH]: 'lUTamkMw7gOzZbFIwmq4',
  [AccentType.IRISH]: 'g3kWXuhrNafLZGm1GXju',
};

const personalizeGrabCustomerName = (
  script: Script,
  newName: string,
): Script => {
  const replaceName = (text?: string) => {
    if (!text) return text;
    return text
      .replace(new RegExp(GRAB_DEFAULT_CUSTOMER_NAME, 'g'), newName)
      .replace(
        new RegExp(GRAB_DEFAULT_CUSTOMER_NAME.toLowerCase(), 'g'),
        newName.toLowerCase(),
      );
  };

  const replaceArray = (values?: string[]) =>
    values?.map((value) => replaceName(value) || value);

  const updatedNodes = Object.fromEntries(
    Object.entries(script.nodes).map(([nodeId, node]) => {
      const updatedExpectedResponse = node.expectedResponse
        ? {
            positive: replaceArray(node.expectedResponse.positive),
            negative: replaceArray(node.expectedResponse.negative),
            neutral: replaceArray(node.expectedResponse.neutral),
          }
        : undefined;

      return [
        nodeId,
        {
          ...node,
          agentMessage: replaceName(node.agentMessage) || node.agentMessage,
          expectedResponse: updatedExpectedResponse,
        },
      ];
    }),
  );

  return {
    ...script,
    nodes: updatedNodes as Script['nodes'],
  };
};

export const use11labsAgentCall = ({
  productCode,
  callType,
  accent = AccentType.DEFAULT,
  mode = CallMode.NORMAL,
  scriptId = 'prudential-annual-checkin',
  parentId,
  userId,
  onClientInfoNeeded,
}: BaseAgentCallProps): AgentCallInterface => {
  const [searchParams] = useSearchParams();
  const customCustomerName =
    searchParams.get('name')?.trim() || null;
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.INITIAL,
  );
  const [error, setError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>(CallState.INCOMING);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<any>(null);
  const [clientInfo, setClientInfo] = useState<any>({});
  const [product, setProduct] = useState<any>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);

  // Call timer state
  const [callDuration, setCallDuration] = useState<number>(0);
  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<any>(null);

  // Transcript state
  const [transcript, setTranscript] = useState<Transcript[]>([]);

  // Script engine state
  const scriptEngineRef = useRef<ScriptEngine | null>(null);
  const [scriptState, setScriptState] = useState<ScriptState | null>(null);
  const [currentScriptNode, setCurrentScriptNode] = useState<ScriptNode | null>(
    null,
  );

  const conversation = useConversation();
  const { isSpeaking } = conversation;

  useEffect(() => {
    if (productCode) {
      initializeProduct();
    }
  }, [productCode]);

  // Set agent info for scripted mode immediately when component loads
  useEffect(() => {
    if (mode === CallMode.SCRIPTED && scriptId) {
      const currentScript = getScript(scriptId);
      const scriptAgentInfo = currentScript?.agentInfo;
      const scriptMetadata = scriptRegistry[scriptId]?.metadata;
      setAgentInfo({
        profile: {
          name: scriptAgentInfo?.name || 'Rachel',
          role: 'Virtual Assistant',
          company: scriptMetadata?.company || 'Prudential',
          avatar:
            scriptAgentInfo?.avatar ||
            'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
        },
      });
    }
  }, [mode, scriptId]);

  useEffect(() => {
    if (callState === CallState.ACTIVE) {
      // Start call timer
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const initializeProduct = async () => {
    try {
      setLoadingState(LoadingState.LOADING_PRODUCT);

      // Fetch product details
      const productData: any = await api
        .get(`api/agent/products/${productCode}`)
        .json();

      setProduct(productData.product);
      setLoadingState(LoadingState.READY);
    } catch (error) {
      console.error('Error initializing product:', error);
      setError('Failed to load product information. Please try again.');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleAnswerCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);

      // Initialize script engine if in scripted mode
      if (mode === CallMode.SCRIPTED) {
        const script = getScript(scriptId || 'prudential-annual-checkin');
        if (!script) {
          throw new Error(`Script not found: ${scriptId}`);
        }

        const personalizedScript =
          scriptId === 'grab-restaurant-onboarding' && customCustomerName
            ? personalizeGrabCustomerName(script, customCustomerName)
            : script;

        scriptEngineRef.current = new ScriptEngine(
          personalizedScript,
          (state) => {
            setScriptState(state);
            setCurrentScriptNode(state.currentNode);
          },
        );

        const initialNode = scriptEngineRef.current.getCurrentNode();
        setCurrentScriptNode(initialNode);
      }

      // Create session with script data
      const sessionData: any = await api
        .post('api/agent/sessions', {
          json: {
            userId,
            productCode,
            callType,
            mode,
            scriptId: mode === CallMode.SCRIPTED ? scriptId : undefined,
            script: scriptEngineRef.current?.getScript(), // Send full script to backend
            ...(parentId && { parentSessionId: parentId }),
          },
        })
        .json();

      setSessionId(sessionData.sessionId);

      // Request microphone access
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const session: any = await api
        .get(`api/agent/sessions/${sessionData.sessionId}`)
        .json();

      // Set client info if available (for follow-up calls)
      if (session.clientInfo) {
        setClientInfo(session.clientInfo);
      }

      // Override agent info for scripted mode
      if (mode === CallMode.SCRIPTED && scriptEngineRef.current) {
        const currentScript = scriptEngineRef.current.getScript();
        const scriptAgentInfo = currentScript?.agentInfo;
        const scriptMetadata =
          scriptRegistry[scriptId || 'prudential-annual-checkin']?.metadata;
        setAgentInfo({
          profile: {
            name: scriptAgentInfo?.name || 'Rachel',
            role: 'Virtual Assistant',
            company: scriptMetadata?.company || 'Prudential',
            avatar:
              scriptAgentInfo?.avatar ||
              'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
          },
        });
      } else {
        setAgentInfo(session.persona);
      }

      // Determine voice ID based on mode and accent
      let voiceId;
      if (mode === CallMode.SCRIPTED && scriptEngineRef.current) {
        const currentScript = scriptEngineRef.current.getScript();
        voiceId = currentScript?.agentInfo?.voiceId || 'ckdz71REaQKVx2gnOQjQ';
      } else if (accent !== AccentType.DEFAULT) {
        voiceId = VOICE_IDS[accent];
      } else {
        voiceId = session.aiConfig.voiceId;
      }

      // Prepare agent prompt based on mode
      let agentPrompt = session.voiceInstructions.chat;
      let firstMessage = session.aiConfig.initialPrompt;

      if (mode === CallMode.SCRIPTED && scriptEngineRef.current) {
        const scriptMessage = scriptEngineRef.current.getScriptForAgent();
        const currentScript = scriptEngineRef.current.getScript();
        const scriptNodes = currentScript?.nodes || {};

        agentPrompt = getScriptPrompt(
          scriptId || 'prudential-annual-checkin',
          scriptMessage,
          scriptNodes,
        );

        firstMessage = scriptMessage;

        // Add contextual response capability to the prompt
        agentPrompt += `\n\nCONTEXTUAL RESPONSES:
When customer asks off-script questions, respond as a professional insurance advisor:
- Keep responses brief and relevant
- After answering, ALWAYS return to the current script step
- Do NOT move to a different script node after answering a question
- Example: If at Step 2 and customer asks "What will we discuss?", answer the question then continue waiting for their response to Step 2's question`;
      }

      const agentId =
        mode === CallMode.SCRIPTED
          ? scriptId === 'grab-restaurant-onboarding'
            ? 'agent_2401k6mgpxsff1wt1zk4h5vs8rf7'
            : SCRIPTED_AGENT_ID
          : session.aiConfig.agentId;

      console.log('Using agent prompt:', agentPrompt);
      console.log('Using first message:', firstMessage);
      console.log('Using voiceId:', voiceId);
      console.log('Using agentId:', agentId);

      // Set call to active state and start timer
      startTimeRef.current = Date.now();
      setLoadingState(LoadingState.ACTIVE);
      setCallState(CallState.ACTIVE);

      // Play TTS first for WRISE script, then start conversation
      if (mode === CallMode.SCRIPTED && scriptId === 'wealth-landscape-forum') {
        try {
          const wriseMessage = `Hi Good Evening ${
            clientInfo?.name || '[Jean]'
          }. This is Emma calling from WRISE Prestige Hong Kong. I'm a virtual assistant to your advisor Stephen, and I hope to share some info on his behalf today. Before I begin, may I highlight that this call is recorded for quality assessment purposes.`;

          // Add TTS message to transcript
          setTranscript((prev) => [
            ...prev,
            {
              source: 'ai',
              message: wriseMessage,
              timestamp: new Date(),
            },
          ]);

          await playScriptFirstMessage(wriseMessage, 'nNMKe8xS6OJl0wUb8Zss');
          console.log('WRISE TTS first message played successfully');
        } catch (error) {
          console.error('Failed to play WRISE TTS first message:', error);
        }
      }

      // Build TTS overrides - include ttsConfig if available (e.g. aia-2 scenario)
      const ttsOverrides: any = {
        voiceId: voiceId || session.aiConfig.voiceId,
      };
      if (session.aiConfig.ttsConfig) {
        const tc = session.aiConfig.ttsConfig;
        if (tc.stability !== undefined) ttsOverrides.stability = tc.stability;
        if (tc.similarityBoost !== undefined)
          ttsOverrides.similarity_boost = tc.similarityBoost;
        if (tc.style !== undefined) ttsOverrides.style = tc.style;
        if (tc.speed !== undefined) ttsOverrides.speed = tc.speed;
        if (tc.useSpeakerBoost !== undefined)
          ttsOverrides.use_speaker_boost = tc.useSpeakerBoost;
      }

      const conversationId = await conversation.startSession({
        agentId,
        overrides: {
          agent: {
            prompt: {
              prompt: agentPrompt,
            },
            firstMessage: firstMessage,
            language: session.aiConfig.language,
          },
          tts: ttsOverrides,
        },
        connectionType: 'webrtc',
        onConnect: () => {
          console.log('Connected to conversation');
        },
        onError: (error) => {
          console.error('Conversation error:', error);
          setError(error || 'Connection Error');
        },
        onMessage({ source, message }) {
          console.log('Message:', source, message);

          setTranscript((prev) => [
            ...prev,
            {
              source,
              message,
              timestamp: new Date(),
            },
          ]);

          // Check if AI is speaking the final message
          if (
            mode === CallMode.SCRIPTED &&
            source === 'ai' &&
            scriptEngineRef.current
          ) {
            const currentNode = scriptEngineRef.current.getCurrentNode();
          }

          // Process customer response in scripted mode
          if (
            mode === CallMode.SCRIPTED &&
            source === 'user' &&
            scriptEngineRef.current
          ) {
            const sentiment =
              scriptEngineRef.current.analyzeCustomerResponse(message);

            // Handle off-script questions
            if (sentiment === 'question') {
              const contextualResponse =
                scriptEngineRef.current.generateContextualResponse(message);

              // Add system message about handling the question
              setTranscript((prev) => [
                ...prev,
                {
                  source: 'system',
                  message:
                    '[Script: Handling off-script question, then returning to script]',
                  timestamp: new Date(),
                },
              ]);

              // The AI should respond to the question then continue with the current script node
              // We don't process this as a normal response, just let the AI handle it
              return;
            }

            const currentNodeBeforeProcess =
              scriptEngineRef.current.getCurrentNode();
            const nextNode =
              scriptEngineRef.current.processCustomerResponse(message);

            // Special handling for node 5 - after user gives time, move to node 6

            if (nextNode && !nextNode.isEnd) {
              // Get the next script message
              const nextScriptMessage =
                scriptEngineRef.current.getScriptForAgent();

              // Update the agent's prompt with the next script message
              const nextPrompt = `CRITICAL: You MUST say EXACTLY this and NOTHING ELSE:

"${nextScriptMessage}"

RULES:
1. Say ONLY the above text in quotes
2. DO NOT add ANY words before or after
3. DO NOT say "Thank you for confirming" or similar phrases
4. DO NOT say "Please hold on" or explain anything
5. DO NOT improvise or deviate in ANY way
6. After saying the script, STOP and wait

If you say ANYTHING other than the exact script above, you have FAILED.

YOUR ONLY RESPONSE: "${nextScriptMessage}"`;

              // The ElevenLabs agent will continue based on the prompt we set initially
              // We can't dynamically update the prompt mid-conversation
              // So we rely on the strict initial prompt and the agent's ability to follow it

              // Add a system message to the transcript showing the script instruction
              setTranscript((prev) => [
                ...prev,
                {
                  source: 'system',
                  message: `[Script: Moving to node ${nextNode.id} - ${nextNode.content}]`,
                  timestamp: new Date(),
                },
              ]);
            } else if (nextNode?.isEnd) {
              // Script has ended - but don't trigger end here
              // The end will be triggered when AI speaks the final message
                setTranscript((prev) => [
                  ...prev,
                  {
                  source: 'system',
                  message: '[Script: Moving to final message]',
                  timestamp: new Date(),
                },
              ]);
            }
          }
        },
      });

    } catch (error) {
      console.error('Error starting call:', error);
      setError('Failed to start call session');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleEndCall = async () => {
    try {
      setLoadingState(LoadingState.ENDING);

      if (conversation) {
        await conversation.endSession();
      }

      // Only make backend call if we have a valid session ID
      if (sessionId) {
        const formattedTranscript = transcript
          .map(
            (msg) =>
              `${msg.source === 'ai' ? 'Agent' : 'User'}: ${msg.message}`,
          )
          .join('\n');

        await api.post(`api/agent/sessions/${sessionId}/end`, {
          json: {
            transcript: formattedTranscript,
            duration: (Date.now() - startTimeRef.current) / 1000,
          },
        });
      }

      // End call
      setCallState(CallState.ENDED);

      // For scripted mode, delay showing thank you dialog to let AI finish speaking
      if (mode === CallMode.SCRIPTED) {
        // The parent component will handle showing the thank you dialog
        if (onClientInfoNeeded) {
          setTimeout(() => {
            onClientInfoNeeded(); // Reuse this callback to show thank you dialog
          }, 3000); // 3 second delay
        }
      } else if (callType === CallType.INITIAL && onClientInfoNeeded) {
        // If this was an initial call in normal mode, show the client info form
        onClientInfoNeeded();
      } else {
        // Show end call notification for other cases
        toast.success('Call ended successfully');
      }
    } catch (error) {
      console.error('Error ending call:', error);

      // Still end the call state even if backend fails
      setCallState(CallState.ENDED);

      // For scripted mode, show thank you dialog after delay
      if (mode === CallMode.SCRIPTED && onClientInfoNeeded) {
        setTimeout(() => {
          onClientInfoNeeded();
        }, 3000);
      } else {
        toast.error('Call ended');
      }
    } finally {
      setLoadingState(LoadingState.READY);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const saveClientInfo = async (
    clientInfoData: any,
    followupDate?: string,
    followupTime?: string,
    followupNotes?: string,
  ) => {
    try {
      const data = {
        ...clientInfoData,
        followupScheduled:
          followupDate && followupTime
            ? new Date(`${followupDate}T${followupTime}`).toISOString()
            : undefined,
        followupNotes,
      };

      // Include accent parameter in the follow-up URL
      const accentParam =
        accent !== AccentType.DEFAULT ? `&accent=${accent}` : '';

      // Save client info
      const response: any = await api
        .post(`api/agent/sessions/${sessionId}/client-info`, {
          json: {
            clientInfo: data,
            additionalParams: accentParam,
          },
        })
        .json();

      toast.success('Follow-up information saved successfully');

      return response.followupUrl;
    } catch (error) {
      console.error('Error saving client info:', error);
      toast.error('Failed to save client information');
      throw error;
    }
  };

  return {
    loadingState,
    error,
    callState,
    sessionId,
    agentInfo,
    clientInfo,
    product,
    isMuted,
    callDuration,
    transcript,
    isSpeaking,
    accent,
    currentScriptNode,
    scriptState,
    handleAnswerCall,
    handleEndCall,
    setClientInfo,
    saveClientInfo,
    formatTime,
    retryInitialization: initializeProduct,
  };
};
